import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Crown, Music, BookOpen, Award, Star, ArrowRight, Waves, Zap, Heart, Users, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageEditProvider, usePageEditMode } from '@/context/PageEditModeContext';
import { PageEditable } from '@/components/edit/PageEditable';
import { PageEditableImage } from '@/components/edit/PageEditableImage';
import { useHero } from '@/hooks/useHero';
import { EditableList } from '@/components/edit/EditableList';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';


// Icon name -> lucide component map so admins can swap icons via a string value
const ICONS = { Waves, Zap, Heart, Star, Music, Users, BookOpen, Crown, Play, ArrowRight };
const IconByName = (name, fallback = Waves) => ICONS[name] || fallback;

// The Mestre Frog page - The Frog Style Capoeira
const MestreBullfrogInner = () => {
  const hero = useHero('mestre-bullfrog');
  const { enabled: editEnabled } = usePageEditMode();
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const frogMovements = [
    { name: "The Jinga", desc: "The foundation - like a frog's subtle weight shifts before a leap", icon: "Waves" },
    { name: "Aú (Cartwheel)", desc: "The frog's rolling escape through water", icon: "Zap" },
    { name: "Negativa", desc: "Low to the ground - the frog's defensive crouch", icon: "Heart" },
    { name: "Rasteira", desc: "The sweeping kick - a frog's powerful leg strike", icon: "Star" },
  ];

  return (
    <div className="min-h-screen bg-black overflow-hidden relative">
      {/* Animated background particles */}
      <div className="absolute inset-0">
        {[...Array(30)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-green-500/30 rounded-full"
            initial={{ 
              x: Math.random() * 100 + 'vw', 
              y: Math.random() * 100 + 'vh',
              scale: Math.random() * 2 + 0.5
            }}
            animate={{ 
              y: [null, Math.random() * -100 + 'vh'],
              opacity: [0.3, 0.8, 0.3]
            }}
            transition={{ 
              duration: 10 + Math.random() * 10,
              repeat: Infinity,
              ease: "linear"
            }}
          />
        ))}
      </div>
      
      {/* Main Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Gradient overlays — scoped to the hero only so they don't dim
            the lower sections (Continue the Journey, footer, etc.) */}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-green-950/30 via-transparent to-black" />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black via-transparent to-black" />
        <div className="max-w-7xl mx-auto px-6 py-20">
          {/* Hero Image - Blended Layout: Frog LEFT, Instruments RIGHT, You CENTER */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative mb-12"
          >
            {/* Subtle glow effect */}
            <div className="absolute inset-0 bg-green-500/5 blur-3xl rounded-full" />
            
            {/* Main image container */}
            <div className="relative max-w-6xl mx-auto rounded-3xl overflow-hidden shadow-xl shadow-green-900/30">
              {/* Base image - Frog LEFT, MORE BERIMBAUS, Rio + NOLA background */}
              <PageEditableImage
                path="hero.bgImage"
                fallback="https://www.casasamba.com/wp-content/uploads/2026/09/ba7e1b7f8460ffe017b50ce5a3cccb869293ab78a9fe6e79f15792f81f04ee11.png"
                alt="Ancient Frog Guardian - Rio meets New Orleans with Capoeira Instruments"
                className="w-full block"
              />
              
              {/* Your photo overlay - CENTERED, LARGER, 100% OPACITY, EMERGING ANIMATION */}
              <div className="absolute inset-0 flex items-end justify-center pointer-events-none">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 50 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ 
                    duration: 3,
                    delay: 0.5,
                    ease: "easeOut"
                  }}
                  className="h-[160%] max-w-[85%] pointer-events-auto"
                >
                  <PageEditableImage
                    path="hero.mestrePhoto"
                    fallback="https://www.casasamba.com/wp-content/uploads/2026/09/ygwclug5_Mestre-Avatar-ID-photo-2.png"
                    alt="Mestre Curtis Pierre"
                    className="h-full w-auto object-contain object-bottom"
                  />
                </motion.div>
              </div>
              
              {/* Floating crown/badge */}
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-green-500 to-emerald-600 text-black px-6 py-2 rounded-full font-black text-sm flex items-center gap-2 shadow-lg z-10"
              >
                <Crown className="w-4 h-4" />
                <PageEditable path="hero.badge" fallback="THE FROG STYLE" />
              </motion.div>
            </div>
          </motion.div>
          
          {/* Text Content - Centered */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-500/30 rounded-full text-green-400 text-sm font-bold mb-6"
            >
              <Star className="w-4 h-4" />
              <PageEditable path="hero.eyebrow" fallback={hero.kicker || "A NEW CAPOEIRA TRADITION"} />
            </motion.div>
            
            <h1 className="text-5xl lg:text-7xl font-black mb-6">
              <span className="text-white"><PageEditable path="hero.title" fallback={hero.title_top || "Kapoeira"} /></span>
              <br />
              <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                <PageEditable path="hero.titleAccent" fallback={hero.title_bottom || "Do Sapo"} />
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-4 max-w-3xl mx-auto">
              <PageEditable
                path="hero.subtitle"
                fallback='"The Way of the Frog" — A revolutionary approach to Capoeira Angola that honors the ancient connection between the frog and the martial art.'
                multiline
              />
            </p>
            
            <PageEditable
              path="hero.body"
              as="p"
              multiline
              className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto"
              fallback="Every Capoeira movement mirrors the frog: the low stance of the Negativa, the explosive leap of the Aú, the patient stillness before the strike. For 38 years, Mestre Curtis Pierre has preserved this sacred knowledge."
            />
            
            {/* Stats */}
            <div className="grid grid-cols-4 gap-6 mb-10 max-w-2xl mx-auto">
              {[
                { value: "38+", label: "Years" },
                { value: "10K+", label: "Students" },
                { value: "324", label: "Pages" },
                { value: "∞", label: "Legacy" }
              ].map((stat, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + idx * 0.1 }}
                  className="text-center"
                >
                  <div className="text-3xl lg:text-4xl font-black text-green-400"><PageEditable path={`stats.${idx}.value`} fallback={stat.value} /></div>
                  <div className="text-sm text-gray-500"><PageEditable path={`stats.${idx}.label`} fallback={stat.label} /></div>
                </motion.div>
              ))}
            </div>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/kapoeira-angola"
                className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-black font-bold text-lg rounded-full hover:shadow-lg hover:shadow-green-500/30 transition-all flex items-center gap-2"
              >
                <Users className="w-5 h-5" />
                <PageEditable path="heroCta.classes" fallback="Start Kapoeira Classes" />
              </Link>
              
              <Link
                to="/kapoeira-book"
                className="px-6 py-4 border border-green-500/30 text-green-400 font-bold rounded-full hover:bg-green-500/10 transition-all flex items-center gap-2"
              >
                <BookOpen className="w-5 h-5" />
                <PageEditable path="heroCta.book" fallback="Read the Book" />
              </Link>

              <Link
                to="/pandeiro-juggling"
                className="px-6 py-4 border border-white/20 text-gray-300 font-bold rounded-full hover:bg-white/5 transition-all flex items-center gap-2"
              >
                <Music className="w-5 h-5" />
                <PageEditable path="heroCta.pandeiro" fallback="Pandeiro" />
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-green-500/50 rounded-full flex items-start justify-center p-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-green-500 rounded-full"
            />
          </div>
        </motion.div>
      </section>
      
      {/* The Frog Connection Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-green-950/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-green-400 font-bold text-sm tracking-wider">
              <PageEditable path="frog.eyebrow" as="span" fallback="THE SACRED CONNECTION" />
            </span>
            <h2 className="text-3xl lg:text-5xl font-black text-white mt-2">
              <PageEditable path="frog.titlePre" as="span" fallback="Why the " /><span className="text-green-400"><PageEditable path="frog.titleAccent" as="span" fallback="Frog" /></span><PageEditable path="frog.titlePost" as="span" fallback="?" />
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <p className="text-lg text-gray-300 leading-relaxed">
                <PageEditable path="frog.para1" as="span" multiline fallback="In the Kongo traditions of Central Africa, the frog represents transformation, adaptability, and hidden power. The enslaved Africans who created Capoeira in Brasil carried these symbols with them." />
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                <PageEditable path="frog.para2" as="span" multiline fallback="Watch a Capoeirista move: the low crouching stance, the explosive unpredictable leaps, the patient waiting before striking — these are the ways of the frog." />
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                <PageEditable path="frog.para3" as="span" multiline fallback="The berimbau's curved bow mimics the arc of a frog's leap. The atabaque's rhythms echo the calls across ancient lagoons. Kapoeira Do Sapo reconnects practitioners to these original roots." />
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <EditableList
                path="frogMovements"
                fallback={frogMovements}
                emptyItem={() => ({ name: 'New movement', desc: 'Describe this movement', icon: 'Waves' })}
                className="grid grid-cols-2 gap-4"
                renderItem={(move, idx) => {
                  const Icon = IconByName(move.icon);
                  return (
                    <div className="bg-green-900/20 border border-green-500/20 rounded-2xl p-6 hover:border-green-500/50 transition-all h-full">
                      <Icon className="w-8 h-8 text-green-400 mb-3" />
                      <h3 className="text-lg font-bold text-white mb-1">
                        <PageEditable path={`frogMovements.${idx}.name`} as="span" fallback={move.name} />
                      </h3>
                      <p className="text-sm text-gray-400">
                        <PageEditable path={`frogMovements.${idx}.desc`} as="span" multiline fallback={move.desc} />
                      </p>
                      <div className="mt-2 text-[10px] uppercase tracking-widest text-green-400/60" style={{ display: editEnabled ? undefined : 'none' }}>
                        icon: <PageEditable path={`frogMovements.${idx}.icon`} as="span" fallback={move.icon || 'Waves'} />
                      </div>
                    </div>
                  );
                }}
              />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Quote Section */}
      <section className="py-20 px-6 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.1),transparent_70%)]" />
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto text-center relative z-10"
        >
          <div className="text-6xl text-green-500/30 mb-4">"</div>
          <blockquote className="text-2xl lg:text-3xl text-white font-light italic mb-6">
            <PageEditable path="quote.text" as="span" multiline fallback="The frog waits in stillness, then moves with explosive power. This is Capoeira. This is life. Learn to wait. Learn to leap." />
          </blockquote>
          <cite className="text-green-400 font-bold">
            <PageEditable path="quote.author" as="span" fallback="— Mestre Curtis Pierre" />
          </cite>
        </motion.div>
      </section>
      
      {/* The Instruments Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-black to-green-950/20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-green-400 font-bold text-sm tracking-wider">
              <PageEditable path="instruments.eyebrow" as="span" fallback="THE SACRED INSTRUMENTS" />
            </span>
            <h2 className="text-3xl lg:text-4xl font-black text-white mt-2">
              <PageEditable path="instruments.titlePre" as="span" fallback="Voices of the " /><span className="text-green-400"><PageEditable path="instruments.titleAccent" as="span" fallback="Jungle" /></span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              <PageEditable path="instruments.description" as="span" multiline fallback="Each instrument carries the spirit of the natural world. In Kapoeira Do Sapo, they represent the sounds of the Amazon lagoon." />
            </p>
          </motion.div>
          
          <EditableList
            path="instruments.items"
            fallback={[
              { 
                name: "Berimbau", 
                role: "The curved bow that sings",
                desc: "Its arc mirrors the frog's leap. Its voice guides the roda like the wind through trees.",
                color: "amber"
              },
              { 
                name: "Atabaque", 
                role: "The heartbeat drum",
                desc: "Deep like the call of frogs across the lagoon at dusk. It grounds the rhythm of the game.",
                color: "green"
              },
              { 
                name: "Pandeiro", 
                role: "The shimmering rain",
                desc: "Quick, precise, unpredictable - like a frog's tongue striking. Learn to juggle, learn to flow.",
                color: "emerald"
              }
            ]}
            emptyItem={() => ({ name: 'New instrument', role: 'Role', desc: 'Describe this instrument', color: 'green' })}
            className="grid md:grid-cols-3 gap-8"
            renderItem={(inst, idx) => (
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className={`p-8 rounded-2xl border transition-all hover:scale-105 h-full ${
                  inst.color === 'amber' 
                    ? 'bg-amber-500/10 border-amber-500/20 hover:border-amber-500/50' 
                    : inst.color === 'green'
                    ? 'bg-green-500/10 border-green-500/20 hover:border-green-500/50'
                    : 'bg-emerald-500/10 border-emerald-500/20 hover:border-emerald-500/50'
                }`}
              >
                <Music className={`w-10 h-10 mb-4 ${
                  inst.color === 'amber' ? 'text-amber-400' :
                  inst.color === 'green' ? 'text-green-400' : 'text-emerald-400'
                }`} />
                <h3 className="text-xl font-bold text-white mb-1">
                  <PageEditable path={`instruments.items.${idx}.name`} as="span" fallback={inst.name} />
                </h3>
                <p className={`text-sm mb-3 ${
                  inst.color === 'amber' ? 'text-amber-400/70' :
                  inst.color === 'green' ? 'text-green-400/70' : 'text-emerald-400/70'
                }`}>
                  <PageEditable path={`instruments.items.${idx}.role`} as="span" fallback={inst.role} />
                </p>
                <p className="text-gray-400">
                  <PageEditable path={`instruments.items.${idx}.desc`} as="span" multiline fallback={inst.desc} />
                </p>
                <div className="mt-2 text-[10px] uppercase tracking-widest text-white/40" style={{ display: editEnabled ? undefined : 'none' }}>
                  color: <PageEditable path={`instruments.items.${idx}.color`} as="span" fallback={inst.color || 'green'} />
                </div>
              </motion.div>
            )}
          />
        </div>
      </section>
      
      {/* CTA Links Section — Continue the Journey */}
      <section id="continue-journey" className="py-20 px-6 bg-black scroll-mt-24">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-bold uppercase tracking-widest mb-4">
              <Users className="w-3.5 h-3.5" />
              <PageEditable path="roda.eyebrow" fallback="Continue the Journey" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-black text-white">
              <PageEditable path="roda.titlePre" as="span" fallback="Step into the " /><span className="text-green-400"><PageEditable path="roda.titleAccent" as="span" fallback="Roda" /></span>
            </h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              <PageEditable path="roda.description" as="span" multiline fallback="Three ways to keep going after the Frog Style. Weekly in-person classes are the fastest path — first one is free." />
            </p>
          </motion.div>
          
          <EditableList
            path="cards"
            fallback={[
              { 
                icon: "Users", 
                title: "Kapoeira Angola Classes", 
                desc: "In-person weekly classes in New Orleans with Mestre Curtis — first class free.",
                link: "/kapoeira-angola",
                color: "green",
                cta: "See Class Schedule"
              },
              { 
                icon: "BookOpen", 
                title: "Kapoeira Study Guide", 
                desc: "324 pages of authentic techniques and philosophy by Mestre Curtis Pierre.",
                link: "/kapoeira-book",
                color: "amber",
                cta: "Read the Book"
              },
              { 
                icon: "Music", 
                title: "Pandeiro Academy", 
                desc: "Master the dying art of Pandeiro juggling, the rhythmic partner of Capoeira.",
                link: "/pandeiro-juggling",
                color: "purple",
                cta: "Start Learning"
              }
            ]}
            emptyItem={() => ({ icon: 'Users', title: 'New card', desc: 'Describe this card', link: '/', color: 'green', cta: 'Learn More' })}
            className="grid md:grid-cols-3 gap-6"
            renderItem={(item, idx) => {
              const Icon = IconByName(item.icon, Users);
              return (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="h-full"
                >
                  <Link
                    to={item.link || '/'}
                    className={`block p-8 rounded-2xl border transition-all hover:scale-105 h-full ${
                      item.color === 'amber' 
                        ? 'bg-amber-500/10 border-amber-500/20 hover:border-amber-500/50' 
                        : item.color === 'green'
                        ? 'bg-green-500/10 border-green-500/20 hover:border-green-500/50'
                        : 'bg-purple-500/10 border-purple-500/20 hover:border-purple-500/50'
                    }`}
                  >
                    <Icon className={`w-10 h-10 mb-4 ${
                      item.color === 'amber' ? 'text-amber-400' :
                      item.color === 'green' ? 'text-green-400' : 'text-purple-400'
                    }`} />
                    <h3 className="text-xl font-bold text-white mb-2">
                      <PageEditable path={`cards.${idx}.title`} as="span" fallback={item.title} />
                    </h3>
                    <p className="text-gray-400 mb-4">
                      <PageEditable path={`cards.${idx}.desc`} as="span" multiline fallback={item.desc} />
                    </p>
                    <span className={`inline-flex items-center gap-2 font-bold ${
                      item.color === 'amber' ? 'text-amber-400' :
                      item.color === 'green' ? 'text-green-400' : 'text-purple-400'
                    }`}>
                      <PageEditable path={`cards.${idx}.cta`} as="span" fallback={item.cta} /> <ArrowRight className="w-4 h-4" />
                    </span>
                    <div className="mt-3 text-[10px] uppercase tracking-widest text-white/40 flex flex-wrap gap-x-3 gap-y-1" style={{ display: editEnabled ? undefined : 'none' }}>
                      <span>link: <PageEditable path={`cards.${idx}.link`} as="span" fallback={item.link || '/'} /></span>
                      <span>icon: <PageEditable path={`cards.${idx}.icon`} as="span" fallback={item.icon || 'Users'} /></span>
                      <span>color: <PageEditable path={`cards.${idx}.color`} as="span" fallback={item.color || 'green'} /></span>
                    </div>
                  </Link>
                </motion.div>
              );
            }}
          />
        </div>
      </section>
      
      {/* Footer Quote */}
      <section className="py-12 px-6 bg-green-950/20 border-t border-green-500/10">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-400 italic">
            <PageEditable path="footerQuote.text" as="span" multiline fallback="&quot;In the lagoon, the frog teaches patience. In the roda, the Capoeirista becomes the frog.&quot;" />
          </p>
          <p className="text-green-400 font-bold mt-2">
            <PageEditable path="footerQuote.author" as="span" fallback="— Kapoeira Do Sapo" />
          </p>
        </div>
      </section>
    </div>
  );
};


const MestreBullfrog = () => (
  <PageEditProvider slug="mestre-bullfrog">
    <MestreBullfrogInner />
    <PageEditToolbar />
  </PageEditProvider>
);

export { MestreBullfrog };
export default MestreBullfrog;
